import { Registry } from '@lightningjs/sdk';
import AbstractAdManager, {
  AdCuePoint,
} from 'components/pages/playback/ad-managers/AbstractAdManager';
import { BcSsaiIntegration } from 'types/brightcove';

const PERIOD_OFFSET = 1;
const POLLING_TIMEOUT = 7000;
const POLLING_INTERVAL = 500;
const STREAM_TIME_OFFSET = 0.1;

export default class BrightcoveAdManager extends AbstractAdManager {
  override readonly adManagerType = 'brightcove';
  integration: BcSsaiIntegration | null = null; // this will be disposed by the player

  private periodStart: number | null = null;
  private periodEnd: number | null = null;
  private periodCue: AdCuePoint | null = null;

  private completedAdPodTime = 0;

  private videoJsInstance: any;

  getCuePoint(time: number): AdCuePoint | null {
    // Check current bounds
    if (
      this.periodStart &&
      this.periodEnd &&
      time >= this.periodStart &&
      time <= this.periodEnd
    ) {
      return this.periodCue;
    }

    if (!this._cuePoints) {
      return null;
    }

    this.updatePeriodState(time);
    return this.periodCue;
  }

  getPreviousCuePoint(time: number): AdCuePoint | null {
    if (!this._cuePoints) return null;
    this.updatePeriodState(time);
    let previousCue: AdCuePoint | null = null;
    for (const cuePoint of this._cuePoints) {
      if (cuePoint.start <= time) {
        if (!previousCue || cuePoint.end > previousCue.end) {
          previousCue = cuePoint;
        }
      }
    }
    return previousCue;
  }

  private updatePeriodState(time: number): void {
    let previousAdEnd = 0;
    let nextAdStart = Infinity;

    // Reset and re-calculate completedAdPodTime
    this.completedAdPodTime = 0;

    // Iterate through cuePoints
    if (!this._cuePoints) {
      return;
    }

    for (const cuePoint of this._cuePoints) {
      if (cuePoint.start <= time && time <= cuePoint.end) {
        // We're in ad, set the period to ad time
        this.periodStart = cuePoint.start;
        this.periodEnd = cuePoint.end;
        this.periodCue = cuePoint;
        return;
      }

      if (cuePoint.end < time) {
        // accumulate ad played from previous cuePoints
        this.completedAdPodTime += cuePoint.end - cuePoint.start;

        // find previous ad's end time
        if (previousAdEnd < cuePoint.end) {
          previousAdEnd = cuePoint.end;
        }
      }

      // find next ad's start time
      if (time < cuePoint.start && cuePoint.start < nextAdStart) {
        nextAdStart = cuePoint.start;
      }
    }

    // We're not in ad, set the period to content time
    this.periodStart = previousAdEnd + PERIOD_OFFSET;
    this.periodEnd =
      nextAdStart === Infinity ? null : nextAdStart - PERIOD_OFFSET;
    this.periodCue = null;
  }

  async onContentLoaded() {
    // The SSAI plugin hasn't been determined but Brightcove seem to use videojs-contrib-ads for ad logic https://videojs.github.io/videojs-contrib-ads/

    // Get our videojs player instance
    const videoJsPlayer = (document.getElementsByTagName('video-js')[0] as any)
      ?.player;

    if (!videoJsPlayer) throw new Error('videoJsPlayer on initialized');

    this.videoJsInstance = videoJsPlayer;
    return this.fetchCuePoints(this.videoJsInstance);
  }

  getContentTime(streamTime: number) {
    if (!this.videoJsInstance) return;

    const absoluteTime =
      this.videoJsInstance
        .ssai()
        .contentTimelineState(streamTime + STREAM_TIME_OFFSET)?.absoluteTime ??
      null;
    return absoluteTime;
  }

  getStreamTime(contentTime: number) {
    // BC SSAI methods like contentTimelineState() doesn't seem to give proper relativeTime when
    // we set up cuePoints in the beginning. Stick to manual calculation.
    this.updatePeriodState(contentTime);
    return contentTime - this.completedAdPodTime;
  }

  getCurrentCuePoint(): AdCuePoint | null {
    const currentTime = this.getRawTime();
    this.updatePeriodState(currentTime);
    return this.periodCue;
  }

  getNextCuePoint(): AdCuePoint | null {
    if (!this._cuePoints) return null;
    const currentTime = this.getRawTime();
    return this.getNextCuePointFrom(currentTime);
  }

  private fetchCuePoints(videoJsPlayer: any): Promise<void> {
    return new Promise(resolve => {
      const cuePointsChanged = (adRolls: any) => {
        const cuePoints = adRolls.map(
          (pod: any) =>
            ({
              start: pod.absoluteStartTime_,
              end: pod.absoluteEndTime_,
              played: false,
            } as AdCuePoint),
        );
        this._cuePoints = cuePoints;
        this.signal('cuePointsChanged', this._cuePoints);
      };

      // Set up polling
      const startTime = Date.now();
      const pollingInterval = Registry.setInterval(() => {
        const timeline = videoJsPlayer.ssai().timeline_; // we aren't supposed to access this, but there doesn't seem to be any other way
        const adRolls = timeline?.linearAdRolls_;

        if (adRolls || Date.now() - startTime >= POLLING_TIMEOUT) {
          if (adRolls) cuePointsChanged(adRolls);
          Registry.clearInterval(pollingInterval);
          resolve();
        }
      }, POLLING_INTERVAL);
    });
  }
}
